import React from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Sparkles } from "lucide-react";

/**
 * A locked action button shown to free users in place of a paid feature
 * (Source, Save, Reset, extra scans). Clicking routes to billing/upgrade.
 * Visually reads as disabled-with-a-lock so it's clearly gated, not broken.
 */
export function LockedButton({ label = "Upgrade", title, className = "", size = "sm", "data-testid": testId }) {
  const nav = useNavigate();
  const pad = size === "sm" ? "px-3 py-1.5 text-sm" : "px-4 py-2 text-sm";
  return (
    <button
      type="button"
      data-testid={testId}
      title={title || "Upgrade to unlock this feature"}
      onClick={(e) => { e.stopPropagation(); nav("/app/billing"); }}
      className={`inline-flex items-center gap-1.5 rounded-md font-medium border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 transition-colors ${pad} ${className}`}
    >
      <Lock className="w-3.5 h-3.5" strokeWidth={2.2} />
      {label}
    </button>
  );
}

/**
 * The neutral third state, shown in place of LockedButton (or of the real
 * control) while we still don't know what this workspace can do — see
 * lib/useEntitlements.js, which fails closed.
 *
 * It must claim NOTHING: no lock icon, no amber, no route to billing. Amber +
 * lock is our "you must pay" vocabulary, and flashing that at a paying customer
 * on every page load is worse than the permissive bug it replaced. So this is
 * the same disabled-neutral grey the brand-reset cooldown already uses, with a
 * quiet pulse that stops under prefers-reduced-motion.
 *
 * Geometry matches LockedButton exactly, so pending → locked is a colour
 * change with zero layout shift.
 */
export function PendingButton({ label = "Checking…", title, className = "", size = "sm", "data-testid": testId }) {
  const pad = size === "sm" ? "px-3 py-1.5 text-sm" : "px-4 py-2 text-sm";
  return (
    <button
      type="button"
      disabled
      aria-busy="true"
      data-testid={testId}
      title={title || "Checking what your plan includes…"}
      className={`inline-flex items-center gap-1.5 rounded-md font-medium border border-lm-border bg-lm-tint text-lm-text3 cursor-default ${pad} ${className}`}
    >
      <span
        aria-hidden="true"
        className="w-3.5 h-3.5 rounded-full bg-lm-text3/25 animate-pulse motion-reduce:animate-none"
      />
      {label}
    </button>
  );
}

/**
 * The FOURTH state: we asked, we failed, and we have stopped asking.
 *
 * PendingButton above says "Checking…" with aria-busy="true". Under
 * `unreachable` both of those are false — nothing is being checked and nothing
 * is busy — and the old code left that lie on screen forever. This is the same
 * neutral geometry with the claim removed.
 *
 * "Paused", not "Unavailable": a paying owner reads "Unavailable" next to a
 * locked feature as "not included in your plan" — an upsell by implication,
 * which the spec forbids. A paying customer must never see "Upgrade" or a lock,
 * in any state, at any frame. "Paused" implies no tier, no permission, and no
 * promise. No amber, no lock icon, no route to billing, and no pulse — the
 * pulse was proof-of-life, and there is no life to prove.
 *
 * The explanation lives in the ONE global banner (§4). This button does not
 * repeat it; it points at it.
 */
export function UnknownButton({ label = "Paused", title, className = "", size = "sm", "data-testid": testId }) {
  const pad = size === "sm" ? "px-3 py-1.5 text-sm" : "px-4 py-2 text-sm";
  return (
    <button
      type="button"
      disabled
      aria-busy="false"
      aria-label="Paused. See the message at the top of the page."
      data-testid={testId}
      title={title || "Paused. See the message at the top of the page."}
      className={`inline-flex items-center gap-1.5 rounded-md font-medium border border-lm-border bg-lm-tint text-lm-text3 cursor-default ${pad} ${className}`}
    >
      <span aria-hidden="true" className="w-3.5 h-3.5 rounded-full bg-lm-text3/25" />
      {label}
    </button>
  );
}

/**
 * Convenience: pick the right neutral control for the current gate state.
 * Call sites that already branch on `loaded` become a one-line change.
 */
export function GateNeutralButton({ unknown, ...props }) {
  return unknown ? <UnknownButton {...props} /> : <PendingButton {...props} />;
}

/** Prominent upgrade CTA card/banner for bigger gates (scan used up, etc.). */
export function UpgradeBanner({ title, body, cta = "Upgrade to Starter", className = "" }) {
  const nav = useNavigate();
  return (
    <div className={`rounded-xl border border-amber-300 bg-gradient-to-br from-amber-50 to-white p-5 ${className}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
          <Sparkles className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-semibold text-lm-text">{title}</h3>
          {body && <p className="text-sm text-lm-text2 mt-1 leading-relaxed">{body}</p>}
          <button
            onClick={() => nav("/app/billing")}
            data-testid="upgrade-cta"
            className="mt-3 inline-flex items-center gap-2 bg-lm-primary text-white text-sm font-semibold px-4 py-2.5 rounded-lg hover:bg-lm-primaryHover transition-colors"
          >
            <Sparkles className="w-4 h-4" /> {cta}
          </button>
        </div>
      </div>
    </div>
  );
}
